import { useEffect, useState } from "react";
import { BACKEND_AI_AGENT_URL } from "../../../../config/variables";

interface TrendingNarrative {
  _id: {
    $oid: string;
  };
  category: string;
  img: string;
  description: string;
  cmcId: string;
  cmcData: {
    avg_price_change: number;
  };
}

const generateRandomPath = (isPositive: boolean) => {
  const points: string[] = [];
  const numPoints = 10;

  // Start point
  points.push(`M-14.5 ${isPositive ? 40 : 15}`);

  // Generate random points that trend in the appropriate direction
  for (let i = 0; i < numPoints; i++) {
    const x = i * 17 + (Math.random() * 8 - 4);

    // Base y progression
    const progressionBase = isPositive
      ? 40 - (i * 35) / numPoints
      : 15 + (i * 35) / numPoints;

    // Add more dramatic random noise
    const noise = Math.random() * 12 - 6;
    const y = Math.max(1, Math.min(55, progressionBase + noise));

    points.push(`L${x} ${y}`);
  }

  // End point with some randomness
  const finalY = isPositive ? 1 + Math.random() * 4 : 51 + Math.random() * 4;
  points.push(`L151.5 ${finalY}`);

  return points.join(" ");
};

export const TrendingCards = () => {
  const [trendingData, setTrendingData] = useState<TrendingNarrative[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        const response = await fetch(
          `${BACKEND_AI_AGENT_URL}/trending-narratives`,
        );
        const data: TrendingNarrative[] = await response.json();

        // Sort by avg_price_change and get top 3 positive trends
        const topTrends = data
          .filter((item) => item.cmcData.avg_price_change > 0)
          .sort(
            (a, b) => b.cmcData.avg_price_change - a.cmcData.avg_price_change,
          )
          .slice(0, 3);

        setTrendingData(topTrends);
      } catch (error) {
        console.error("Failed to fetch trending narratives:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrendingData();
  }, []);

  if (isLoading) {
    return <div className="flex gap-4"></div>;
  }

  return (
    <div className="flex gap-4">
      {trendingData.map((narrative, index) => (
        <div
          key={narrative.cmcId}
          className="w-1/3 h-32 rounded-lg shadow-lg relative overflow-hidden bg-cover bg-center"
          style={{
            backgroundColor: "#09141E",
            backgroundImage: `url(${narrative?.img})`,
          }}
        >
          <div className="absolute bg-[#09141E] bg-opacity-80 z-10 right-0 left-0 bottom-0 top-0"></div>
          <div className="absolute top-2 left-4 text-blue font-bold text-base z-20 text-[#007DFF]">
            {narrative.category}
          </div>
          <div className="absolute bottom-2 right-4 text-green text-sm font-semibold z-20 text-[#4CAF50]">
            +{narrative.cmcData.avg_price_change.toFixed(2)}%
          </div>
          <svg
            className="absolute inset-0 m-auto z-20"
            width="137"
            height="56"
            viewBox="0 0 137 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d={generateRandomPath(narrative.cmcData.avg_price_change > 0)}
              stroke={
                narrative.cmcData.avg_price_change > 0 ? "#4CAF50" : "#FF4444"
              }
              strokeWidth="1.5"
            />
          </svg>
        </div>
      ))}
    </div>
  );
};

export default TrendingCards;
